import React from 'react';
import { TwinEntity } from '@repo/backend-types';
import { TwinEntityType } from '@repo/frontend-types';
import { isArealessSceneMode, stringToTwinEntityType } from '@repo/front-end-utils';
import { useModeContext } from '@repo/front-end-state';
import { Area } from './twinEntities/area/Area';
import { TempAsset } from './twinEntities/asset/Asset';
import { HandleAsset } from './twinEntities/asset/HandleAsset';
import { EventSpace } from './twinEntities/event/EventSpace';
import { Site } from './twinEntities/site/Site';
import { Structure } from './twinEntities/structure/Structure';
import { Wall } from '@repo/ui';
import { Floor } from './twinEntities/floor/Floor';

type Props = {
  entity: TwinEntity;
  mapDiv: HTMLDivElement;
  loadingList: Map<string, boolean>;
  depth: number; // the depth we're at in the twin heirachy
  lineage: TwinEntity[]; // the acenstry of this entity, oldest first
  mapOrigin: { latitude: number; longitude: number }; // only one, set it in MapCanvas
};

export function TwinEntity3D({
  entity,
  mapDiv,
  loadingList,
  depth,
  lineage,
  mapOrigin,
}: Props): React.JSX.Element {
  // Equipment mode renders an area-less scene (spec FR9): suppress the Area's capacity tint,
  // outline, and Label3D while preserving recursion into child assets. Called unconditionally at
  // the top so hook order is stable regardless of entity type.
  const { appMode } = useModeContext();
  const entityType = stringToTwinEntityType(entity.type.name);
  const ThisEntity = ({ children }: { children?: React.JSX.Element | React.JSX.Element[] }) => {
    switch (entityType) {
      case TwinEntityType.EVENT:
        return (
          <EventSpace entity={entity} lineage={lineage} mapDiv={mapDiv} loadingList={loadingList} />
        );

      case TwinEntityType.AREA:
        // In an area-less mode (Equipment), drop the <Area> wrapper (its Box tint/outline +
        // the Label3D rendered inside Box) but keep rendering its children — nested assets
        // still show. RunningTrackArea is dispatched inside Area.tsx, so it is caught by this seam.
        if (isArealessSceneMode(appMode)) return <>{children}</>;
        return (
          <Area entity={entity} lineage={lineage} mapDiv={mapDiv} depth={depth}>
            {children}
          </Area>
        );

      case TwinEntityType.ASSET:
        return entity.modelFile ? (
          <HandleAsset
            entity={entity}
            mapDiv={mapDiv}
            loadingList={loadingList}
            lineage={lineage}
          />
        ) : (
          <TempAsset entity={entity} mapDiv={mapDiv} lineage={lineage} />
        );

      case TwinEntityType.CATEGORY:
        // TODO: no existing entities in the graphs
        return <>{children}</>;

      case TwinEntityType.FLOOR:
        // N.B. structures handle THEIR floors differently
        return (
          // this is a bit hacky but floor should have lineage of just 1 level and its the site
          <Floor siteID={lineage[0].bID} entity={entity} altitude={entity.altitude ?? 0.01}>
            {children}
          </Floor>
        );

      case TwinEntityType.STATION:
        // N.B. this is actually handled within EVENT
        return null;

      case TwinEntityType.OPERATOR:
        // N.B. this is actually handled within EVENT
        return null;

      case TwinEntityType.STRUCTURE:
        return (
          <Structure
            entity={entity}
            mapOrigin={mapOrigin}
            depth={depth}
            childEntities={entity.children!}
            modelFile={entity.modelFile!}
            mapDiv={mapDiv}
            loadingList={loadingList}
            lineage={lineage}
          />
        );

      case TwinEntityType.SITE:
        return (
          <Site entity={entity} orientation={entity.rotationY ?? 0}>
            {children}
          </Site>
        );

      case TwinEntityType.WALL:
        return <Wall entity={entity} depth={depth} />;
      case TwinEntityType.NFAREA:
        // TODO: no existing entities in the graphs
        return <>{children}</>;
      case TwinEntityType.ZONE:
        // TODO: no existing entities in the graphs
        return <>{children}</>;
      case TwinEntityType.CHUTE:
        // TODO: no existing entities in the graphs
        return <>{children}</>;
      case TwinEntityType.TICKET:
        // TODO: no existing entities in the graphs
        return <>{children}</>;
      case TwinEntityType.UNKNOWN:
        // TODO: no existing entities in the graphs
        return <>{children}</>;
    }
  };

  if (entity.children && entityType !== TwinEntityType.STRUCTURE) {
    // structures inspect their own children
    return (
      <ThisEntity>
        {entity.children.map((childEntity, i) => {
          return (
            <TwinEntity3D
              key={(childEntity as any).bID ?? (childEntity as any).id ?? i}
              entity={childEntity}
              mapDiv={mapDiv}
              loadingList={loadingList}
              depth={depth + 1}
              lineage={[...lineage, entity]}
              mapOrigin={mapOrigin}
            />
          );
        })}
      </ThisEntity>
    );
  } else {
    return <ThisEntity />;
  }
}
